export interface IProcessedImage {
  blob: Blob
  width: number
  height: number
}

// Client-side, canvas-based resize — ports Sampler's getResizedDataURL
// (milva-pro-v3/src/store/images/_helpers.js) exactly: scales so the
// *shorter* side becomes exactly minSize, which can upscale a source image
// smaller than minSize, and leaves the longer side uncapped.
//
// This looks backwards for a "shrink large photos" use case, but it's
// deliberate parity with Sampler, and it's *why* the combination with a
// subsequent square crop (imageCrop.ts) always lands on exactly
// minSize x minSize — the crop takes min(resizedWidth, resizedHeight), which
// this resize step guarantees equals minSize. For an uncropped ("original")
// use — e.g. floor plans — minSize is the only control; there's no separate
// max-size cap in Sampler's own behaviour, so there isn't one here either.
// Returns the resulting dimensions alongside the blob so callers can
// populate IImageBase.width/height without a second, redundant image load.
export async function resizeImageToMinSize(blob: Blob, minSize: number): Promise<IProcessedImage> {
  const objectUrl = URL.createObjectURL(blob)

  try {
    const img = await loadImage(objectUrl)
    const ratio = minSize / Math.min(img.width, img.height)

    const canvas = document.createElement('canvas')
    canvas.width = Math.round(img.width * ratio)
    canvas.height = Math.round(img.height * ratio)

    const context = canvas.getContext('2d')
    if (!context) throw new Error('Canvas 2D context unavailable')

    context.drawImage(img, 0, 0, canvas.width, canvas.height)

    const resizedBlob = await canvasToBlob(canvas, blob.type || 'image/jpeg')
    return { blob: resizedBlob, width: canvas.width, height: canvas.height }
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Failed to load image for resizing'))
    img.src = src
  })
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Failed to encode resized image'))
        return
      }
      resolve(blob)
    }, type)
  })
}
